import { useDispatch } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';
import { ChangeEvent, useState } from 'react';
import { unwrapResult } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import dayjs, { Dayjs } from 'dayjs';

import { CustomAlert, CustomButton, CustomInput, CustomSelect, Picker } from '../../../../../shared/uiComponents';
import { dateNormalizer, errorNormalizer, validateDecimalNumber } from '../../../../../shared/Helpers/functions';
import { useCompareDates } from '../../../../../shared/Helpers/hooks';

import { DispatchProperties, useSelector } from '../../../../../redux/store';
import { AuthorizationProperties } from '../../../../../redux/API/ClientAPIHelpers/insuranceAuthorizationProperties';
import {
	getInsuranceAuthorizationSessionTypes,
	getInsuranceAuthorizations,
	saveAssessmentInsuranceAuthorizations,
} from '../../../../../redux/State/clientSlice/insuranceAuthorizationSlice';

const AssessmentContent = ({ onClose }: { onClose: () => void }) => {
	const dispatch = useDispatch<DispatchProperties>();
	const { clientId } = useParams();
	const [params] = useSearchParams();

	const today = new Date();
	const todayDayjs = dayjs(today);
	const futureDateMonth = dayjs(today).add(1, 'month');

	const [startDateValue, setStartDateValue] = useState<Dayjs>(todayDayjs);
	const [endDateValue, setEndDateValue] = useState<Dayjs>(futureDateMonth);
	const [totalHours, setTotalHours] = useState<string>('0');
	const [loading, setLoading] = useState<boolean>(false);

	const sessionTypes = useSelector((state) => state.authorization.insuranceAuthorizationSessionTypes);
	const { hasError, message } = useCompareDates(startDateValue, endDateValue);

	const onChangeAssessmentStartDate = (value: Dayjs | null) => {
		if (!value) return;
		setStartDateValue(value);
	};

	const onChangeAssessmentEndDate = (value: Dayjs | null) => {
		if (!value) return;
		setEndDateValue(value);
	};

	const fetchAuthorizations = () => {
		const page = params.get('page') || '1';
		const pageSize = params.get('pageSize') || '8';
		if (!clientId) return;
		dispatch(getInsuranceAuthorizations({ clientId, page, pageSize }));
	};

	const saveAuthorizationAssessment = () => {
		if (!startDateValue || !endDateValue || !clientId) return;

		const startDate = dateNormalizer(startDateValue);
		const endDate = dateNormalizer(endDateValue);

		const data: AuthorizationProperties = {
			startDate,
			endDate,
			authorizationDetails: [
				{
					authorizationSessionTypeId: sessionTypes[0].id,
					totalHours: parseFloat(totalHours),
					frequency: 2,
					hoursPerWeek: null,
				},
			],
		};
		setLoading(true);
		dispatch(saveAssessmentInsuranceAuthorizations({ clientId, data }))
			.then(unwrapResult)
			.then(() => {
				onClose();
				toast('Success');
				fetchAuthorizations();
			})
			.catch(errorNormalizer)
			.finally(() => setLoading(false));
	};

	const onTotalHoursChange = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		if (!validateDecimalNumber(value)) return;
		setTotalHours(value);
	};

	return (
		<>
			<Picker.CustomDate
				label="Start Date:"
				value={startDateValue}
				onChange={onChangeAssessmentStartDate}
				className="marginBottom16 marginTop16"
			/>
			<Picker.CustomDate
				label="End Date:"
				value={endDateValue}
				onChange={onChangeAssessmentEndDate}
				className="marginBottom16"
				errorMessage={message}
				error={hasError}
			/>
			<div className="insuranceAuthorizationInputsRow marginBottom16">
				<CustomSelect
					label="Session type"
					data={sessionTypes}
					value={sessionTypes[0]?.id}
					setValue={() => null}
					disabled
				/>
				<CustomInput label="Total hours" value={totalHours} onChange={onTotalHoursChange} />
			</div>
			<CustomButton
				title="Save Assessment"
				onClick={saveAuthorizationAssessment}
				disabled={hasError || !totalHours}
				loading={loading}
			/>
		</>
	);
};

const AddAssessmentInsuranceAuthorization = () => {
	const dispatch = useDispatch<DispatchProperties>();
	const [open, setOpen] = useState<boolean>(false);

	const openHandler = () => {
		dispatch(getInsuranceAuthorizationSessionTypes('1'));
		setOpen(true);
	};

	const closeHandler = () => setOpen(false);

	return (
		<>
			<CustomButton title="Add Assessment" onClick={openHandler} />
			<CustomAlert
				title="Assessment"
				open={open}
				onClose={closeHandler}
				contentHeight="550px"
				Content={({ onClose }) => <AssessmentContent onClose={onClose} />}
			/>
		</>
	);
};

export default AddAssessmentInsuranceAuthorization;
